import { forwardRef, useEffect, useRef, useState } from "react";
import { masterApi, apiError, formatPhone } from "@/lib/api";
import { useUnit } from "@/pages/Master/MasterApp";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExcelBtn } from "@/lib/excelDownload";
import { modal } from "@/lib/modal";
import { atalhoPeriodoBR } from "@/lib/periodoBR";
import { comPagina, normalizaPaginado } from "@/lib/paginacao";
import { fmtDataHoraBR } from "@/lib/indicacaoWeb";
import Pagination from "@/components/crud/Pagination";
import {
  SEGMENTOS, CATEGORIAS, CATEGORIA_LBL, CANAL_ENVIO_LBL, urlEnvios, qsEnvios, totaisEnvios,
} from "@/lib/campanhas";
import { ENVIO_ROTA_ID, ENVIO_ROTA_NOME } from "@/lib/rotaFidelizacao";
import { ScrollText } from "lucide-react";

/**
 * Fase 5 (pedido de 19/10, itens 5, 10 e 11) — histórico de envios das campanhas, um registro por
 * mensagem (campanha_envios), com os filtros pedidos pelo cliente: unidade, categoria, período (dia
 * de Brasília, abre em Hoje) e resultado; paginação e Excel. "Todas as campanhas" usa
 * GET /master/campanhas/envios; uma campanha usa GET /master/campanhas/{id}/envios.
 * Os envios manuais da tela da Rota (verificação final da Fase 5) entram como a campanha
 * "manual_rota" ("Envios manuais da Rota").
 */

const ATALHOS = [["hoje", "Hoje"], ["ontem", "Ontem"], ["7d", "7 dias"], ["30d", "30 dias"], ["mes", "Este mês"]];
const RESULTADOS = [["all", "Todos"], ["true", "Enviado"], ["false", "Falha"]];
const inputCls = "mt-1 w-full h-9 rounded-md bg-white border border-neutral-200 px-2 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

const CampanhaEnvios = forwardRef(function CampanhaEnvios({ campanhas = [], campanhaId = "", onCampanha }, ref) {
  const { unitId: unitTopo, unidades = [] } = useUnit() || {};
  const [unidade, setUnidade] = useState(unitTopo && unitTopo !== "all" ? unitTopo : "all");
  const inicial = atalhoPeriodoBR("hoje");
  const [inicio, setInicio] = useState(inicial.inicio);
  const [fim, setFim] = useState(inicial.fim);
  const [categoria, setCategoria] = useState("all");
  const [resultado, setResultado] = useState("all");
  const [dados, setDados] = useState({ items: [], total: 0, page: 1, total_pages: 0, totais: null });
  const seq = useRef(0);

  // O seletor do topo também muda a unidade do filtro (só quando ele muda).
  const topoAnterior = useRef(unitTopo);
  useEffect(() => {
    if (topoAnterior.current === unitTopo) return;
    topoAnterior.current = unitTopo;
    setUnidade(unitTopo && unitTopo !== "all" ? unitTopo : "all");
  }, [unitTopo]);

  const periodoValido = !!(inicio && fim && inicio <= fim);
  const atalhoAtivo = (ATALHOS.find(([k]) => atalhoPeriodoBR(k).inicio === inicio && atalhoPeriodoBR(k).fim === fim) || [null])[0];
  const qs = qsEnvios({ unitId: unidade, categoria, inicio, fim, sucesso: resultado });
  const base = urlEnvios(campanhaId);

  const carregar = async (page = 1) => {
    const minha = ++seq.current;
    try {
      const { data } = await masterApi.get(comPagina(`${base}?${qs}`, page));
      if (minha !== seq.current) return;
      const pg = normalizaPaginado(data, page);
      setDados({ items: pg.items, total: pg.total, page: pg.page, total_pages: pg.total_pages, totais: totaisEnvios(data) });
    } catch (e) { if (minha === seq.current) modal.error(apiError(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (periodoValido) carregar(1); }, [campanhaId, unidade, categoria, resultado, inicio, fim]);

  const nomeCampanha = (e) => e.campanha_nome || (e.campanha_id === ENVIO_ROTA_ID ? ENVIO_ROTA_NOME : null)
    || campanhas.find((c) => c.id === e.campanha_id)?.nome || "—";
  const nomeUnidade = (e) => e.unit_nome || unidades.find((u) => u.id === e.unit_id)?.nome || "—";
  const todas = !campanhaId;
  const colunas = todas ? 8 : 7;

  return (
    <section ref={ref} className="space-y-2 scroll-mt-24" data-testid="camp-envios">
      <div className="flex items-center gap-2 flex-wrap">
        <ScrollText size={15} className="text-[color:var(--brand-blue)]" />
        <h2 className="font-serif-display text-lg mr-auto">Histórico de envios</h2>
        <ExcelBtn testid="camp-envios-excel" url={`${base}/excel?${qs}`}
                  filename={todas ? "campanhas_envios.xlsx" : "campanha_envios.xlsx"} />
      </div>
      <p className="text-[11px] text-neutral-500">
        Um registro por mensagem enviada (ou com falha). Período no dia de Brasília.
      </p>

      <div className="card-soft rounded-xl p-3 flex flex-wrap gap-3 items-end" data-testid="camp-envios-filtros">
        <div className="min-w-[200px]">
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Campanha</label>
          <select data-testid="camp-envios-campanha" value={campanhaId} onChange={(e) => onCampanha && onCampanha(e.target.value)} className={inputCls}>
            <option value="">Todas as campanhas</option>
            {campanhas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            <option value={ENVIO_ROTA_ID}>{ENVIO_ROTA_NOME}</option>
          </select>
        </div>
        <div className="min-w-[160px]">
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Unidade</label>
          <select data-testid="camp-envios-unidade" value={unidade} onChange={(e) => setUnidade(e.target.value)} className={inputCls}>
            <option value="all">Todas as unidades</option>
            {unidades.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
          </select>
        </div>
        <div className="min-w-[160px]">
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Categoria</label>
          <select data-testid="camp-envios-categoria" value={categoria} onChange={(e) => setCategoria(e.target.value)} className={inputCls}>
            <option value="all">Todas as categorias</option>
            {CATEGORIAS.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
          </select>
        </div>
        <div className="min-w-[120px]">
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Resultado</label>
          <select data-testid="camp-envios-resultado" value={resultado} onChange={(e) => setResultado(e.target.value)} className={inputCls}>
            {RESULTADOS.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">De</label>
          <input type="date" data-testid="camp-envios-inicio" value={inicio} onChange={(e) => setInicio(e.target.value)}
                 className="mt-1 h-9 rounded-md bg-white border border-neutral-200 px-2 text-sm block" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">até</label>
          <input type="date" data-testid="camp-envios-fim" value={fim} onChange={(e) => setFim(e.target.value)}
                 className="mt-1 h-9 rounded-md bg-white border border-neutral-200 px-2 text-sm block" />
        </div>
        <div className="flex flex-wrap gap-1">
          {ATALHOS.map(([k, lbl]) => (
            <button key={k} type="button" data-testid={`camp-envios-atalho-${k}`}
                    onClick={() => { const a = atalhoPeriodoBR(k); setInicio(a.inicio); setFim(a.fim); }}
                    className={`h-9 px-2.5 text-[11px] font-medium ${atalhoAtivo === k ? "btn-primary-blue" : "btn-ghost"}`}
                    style={{ borderRadius: 8 }}>
              {lbl}
            </button>
          ))}
        </div>
        {!periodoValido && (
          <div className="text-[12px] text-rose-700 w-full" data-testid="camp-envios-periodo-invalido">
            Informe as duas datas, com o início antes do fim.
          </div>
        )}
      </div>

      {dados.totais && (
        <div className="flex flex-wrap gap-1.5" data-testid="camp-envios-totais">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-neutral-200 bg-white text-[11px] text-neutral-700">
            Enviados: <b data-testid="camp-envios-total-sucesso">{dados.totais.sucesso}</b>
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-neutral-200 bg-white text-[11px] text-neutral-700">
            Falhas: <b data-testid="camp-envios-total-falha">{dados.totais.falha}</b>
          </span>
        </div>
      )}

      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-neutral-50">
            <TableHead className="text-[10px] uppercase tracking-widest">Data/hora</TableHead>
            {todas && <TableHead className="text-[10px] uppercase tracking-widest">Campanha</TableHead>}
            <TableHead className="text-[10px] uppercase tracking-widest">Cliente</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Unidade</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Categoria</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Canal</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Resultado</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Detalhe</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {dados.items.map((e, i) => (
              <TableRow key={e.id || `${e.criado_em}-${i}`} data-testid={`camp-envio-${e.id || i}`}>
                <TableCell className="text-[12px] whitespace-nowrap">{fmtDataHoraBR(e.criado_em)}</TableCell>
                {todas && (
                  <TableCell className="text-[12px]">
                    <div>{nomeCampanha(e)}</div>
                    {e.segmento && <div className="text-[10.5px] text-neutral-500">{SEGMENTOS[e.segmento] || e.segmento}</div>}
                  </TableCell>
                )}
                <TableCell className="text-[12px]">
                  <div>{e.cliente_nome || "—"}</div>
                  {e.cliente_telefone && <div className="text-[10.5px] text-neutral-500 whitespace-nowrap">{formatPhone(e.cliente_telefone)}</div>}
                </TableCell>
                <TableCell className="text-[12px]">{nomeUnidade(e)}</TableCell>
                <TableCell className="text-[12px]">{CATEGORIA_LBL[e.categoria] || e.categoria || "—"}</TableCell>
                <TableCell className="text-[12px]">{CANAL_ENVIO_LBL[e.canal] || e.canal || "—"}</TableCell>
                <TableCell>
                  <span className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${e.sucesso === false ? "bg-rose-100 text-rose-800" : "bg-emerald-50 text-emerald-700"}`}>
                    {e.sucesso === false ? "Falha" : "Enviado"}
                  </span>
                </TableCell>
                <TableCell className="text-[11.5px] text-neutral-600 max-w-[260px] truncate" title={e.detalhe || ""}>{e.detalhe || "—"}</TableCell>
              </TableRow>
            ))}
            {dados.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={colunas} className="text-center text-neutral-500 py-8">Nenhum envio no período.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pagination page={dados.page || 1} totalPages={dados.total_pages || 0} total={dados.total || 0}
                  onPage={carregar} unitLabel="envios" />
    </section>
  );
});

export default CampanhaEnvios;
